import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { allArticles } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/seo/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL;

const articleHeroImages: Record<string, string> = {
  'art-hero-guide-2026': 'https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20large%20monitor%20displaying%20product%20spreadsheet%20and%20fashion%20items%20around%2C%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20professional%20editorial%20photography%2C%20clean%20minimal%20aesthetic%2C%20organized%20composition&width=1440&height=600&seq=art-hero-guide-2026&orientation=landscape',
  'art-hero-sneaker': 'https://readdy.ai/api/search-image?query=Artistic%20sneaker%20collection%20display%20with%20three%20different%20shoes%20on%20illuminated%20pedestal%2C%20soft%20shadow%20studio%20lighting%2C%20editorial%20sneaker%20culture%20photography%2C%20warm%20neutral%20backdrop%2C%20premium%20athletic%20aesthetic&width=1440&height=600&seq=art-hero-sneaker&orientation=landscape',
  'art-hero-sicherheit': 'https://readdy.ai/api/search-image?query=Clean%20workspace%20with%20security%20shield%20hologram%20concept%20floating%20above%20laptop%2C%20soft%20blue%20gray%20ambient%20lighting%2C%20trust%20and%20protection%20theme%2C%20modern%20corporate%20editorial%20photography%2C%20calm%20confident%20atmosphere&width=1440&height=600&seq=art-hero-sicherheit&orientation=landscape',
  'art-hero-versand': 'https://readdy.ai/api/search-image?query=Stylized%20cardboard%20shipping%20boxes%20stacked%20with%20world%20map%20and%20route%20lines%20projected%20on%20wall%20behind%2C%20warm%20industrial%20lighting%2C%20logistics%20concept%20photography%2C%20soft%20warm%20tones%2C%20modern%20shipping%20aesthetic&width=1440&height=600&seq=art-hero-versand&orientation=landscape',
  'art-hero-vergleich': 'https://readdy.ai/api/search-image?query=Two%20laptops%20side%20by%20side%20showing%20different%20platform%20interfaces%20on%20warm%20wooden%20desk%2C%20comparison%20concept%2C%20soft%20natural%20daylight%2C%20clean%20modern%20workspace%2C%20balanced%20professional%20photography&width=1440&height=600&seq=art-hero-vergleich&orientation=landscape',
  'art-hero-anfaenger': 'https://readdy.ai/api/search-image?query=Open%20beginner%20guide%20book%20with%20colorful%20tabs%20and%20handwritten%20notes%20next%20to%20laptop%20on%20soft%20pink%20blush%20desk%2C%20welcoming%20learning%20atmosphere%2C%20warm%20morning%20sunlight%2C%20cozy%20organized%20workspace%2C%20approachable%20lifestyle%20photography&width=1440&height=600&seq=art-hero-anfaenger&orientation=landscape',
  'art-hero-produkte': 'https://readdy.ai/api/search-image?query=Treasure%20chest%20open%20with%20fashion%20accessories%20spilling%20out%20onto%20warm%20sand%20colored%20surface%2C%20discovery%20and%20find%20concept%2C%20dramatic%20golden%20side%20lighting%2C%20editorial%20product%20treasure%20photography%2C%20exciting%20reveal%20atmosphere&width=1440&height=600&seq=art-hero-produkte&orientation=landscape',
  'art-hero-kostenlos': 'https://readdy.ai/api/search-image?query=Bright%20airy%20workspace%20with%20laptop%20showing%20free%20tag%20and%20colorful%20spreadsheet%2C%20surrounded%20by%20neatly%20arranged%20affordable%20fashion%20items%2C%20fresh%20morning%20light%2C%20accessible%20budget-friendly%20concept%2C%20clean%20positive%20photography&width=1440&height=600&seq=art-hero-kostenlos&orientation=landscape',
  'art-hero-fashion': 'https://readdy.ai/api/search-image?query=Artistic%20fashion%20editorial%20flatlay%20with%20fabric%20swatches%20polaroid%20photos%20and%20style%20accessories%20on%20warm%20terracotta%20surface%2C%20soft%20afternoon%20light%2C%20creative%20styling%20concept%2C%20bohemian%20chic%20photography%2C%20rich%20textures%20and%20warm%20tones&width=1440&height=600&seq=art-hero-fashion&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">Artikel nicht gefunden</h1>
          <p className="text-sm text-foreground-500 mb-6">Der gesuchte Artikel existiert leider nicht.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Zurück zur Startseite
          </Link>
        </div>
      </main>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${siteUrl}/artikel/${article.slug}/#article`,
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'OOPBuySpread.de',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
      },
    },
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/artikel/${article.slug}`,
    },
    inLanguage: 'de-DE',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: 'OOPBuySpread.de',
    },
  };

  return (
    <>
      <JsonLd data={articleJsonLd} />
      <main className="min-h-screen bg-background-50">
        <Navbar />

      <article className="pt-24 md:pt-28 pb-16 md:pb-24">
        <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Zurück zur Startseite
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight text-balance">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mt-4 leading-relaxed">
              {article.description}
            </p>

            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-400">Letzte Aktualisierung: {article.date}</p>
              </div>
            </div>
          </div>

          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={articleHeroImages[article.imageSeq] || articleHeroImages['art-hero-guide-2026']}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground-800">Hat dir dieser Guide geholfen?</p>
                <p className="text-xs text-foreground-400 mt-1">Teile ihn mit anderen OOPBuy-Nutzern.</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  Zur Startseite
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  OOPBuy Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <p className="text-xs text-foreground-400 mb-3">Zurück zur Übersicht:</p>
              <Link
                to="/#ratgeber"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Alle Guides &amp; Artikel
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
    </>
  );
}